/**
 * Serviço de API para comunicação com o backend EduRPG
 */
import type { GameTemplate, Mission, MissionSession } from '@/types'

// Detectar URL base da API baseado no ambiente
const getApiBaseUrl = () => {
  // Se estiver em produção (HTTPS ou domínio não-localhost), usar URL relativa
  if (window.location.protocol === 'https:' || window.location.hostname !== 'localhost') {
    return `${window.location.protocol}//${window.location.hostname}/api`;
  }
  // Em desenvolvimento local, usar localhost:5004
  return 'http://localhost:5004/api';
};

// Detectar URL base para arquivos estáticos (imagens, vídeos, etc)
const getStaticBaseUrl = () => {
  if (window.location.protocol === 'https:' || window.location.hostname !== 'localhost') {
    return `${window.location.protocol}//${window.location.hostname}`;
  }
  return 'http://localhost:5004';
};

export const API_BASE_URL = getApiBaseUrl();
export const STATIC_BASE_URL = getStaticBaseUrl();

// Logs apenas em desenvolvimento
if (import.meta.env.DEV) {
  console.log('API Base URL configurada para:', API_BASE_URL);
  console.log('Static Base URL configurada para:', STATIC_BASE_URL);
}

/**
 * Converte uma URL relativa de mídia para URL absoluta
 * Funciona em desenvolvimento (localhost) e produção (VPS)
 * 
 * @param url URL relativa (ex: /static/uploads/...) ou absoluta
 * @returns URL absoluta completa
 */
export function getMediaUrl(url: string | null | undefined): string {
  if (!url) return '';

  // Remover localhost:5004 se presente (dados antigos)
  let cleanUrl = url.replace('http://localhost:5004', '').replace('https://localhost:5004', '');

  // Se já é URL absoluta válida (não localhost), retornar
  if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
    return cleanUrl;
  }

  // Garantir que começa com /
  if (!cleanUrl.startsWith('/')) {
    cleanUrl = '/' + cleanUrl;
  }

  return `${STATIC_BASE_URL}${cleanUrl}`;
}

// Classe de erro personalizada para API
export class APIError extends Error {
  constructor(message: string, public status: number) {
    super(message)
    this.name = 'APIError'
  }
}

// Interface para resposta de autenticação
export interface AuthResponse {
  access_token: string
  token_type: string
  user: User
}

// Interface para usuário
export interface User {
  id: string
  username: string
  email: string
  full_name: string
  role: 'professor' | 'estudante'
  is_active: boolean
  created_at: string
  avatar_url?: string  // URL da foto de perfil
  theme?: 'azul' | 'manuscrito' | 'arcano' | 'sinal'  // tema visual da conta
  auth_provider?: 'local' | 'google'
}

// Interface para mesa/campanha
export interface Table {
  id: string
  name: string
  description: string
  subject: string
  image_url?: string
  creator_id: string
  created_at: string
  is_active: boolean
  access_code?: string  // Código de acesso alfanumérico de 6 caracteres
  member_count?: number
  character_count?: number
  user_role?: 'mestre' | 'jogador' | 'visitante'  // Papel do usuário atual na mesa
}

// Interface para personagem
export interface Character {
  id: string
  name: string
  description: string
  className: string
  tableId: string
  userId: string
  imageUrl?: string
  level: number
  hpTotal: number
  hpCurrent: number
  attributes: Record<string, number>  // Atributos dinâmicos
  points: number
}

// Interface para membro
export interface Member {
  id: string
  tableId?: string
  userId: string
  userFullName: string
  role: 'mestre' | 'jogador' | 'npc'
  joinedAt?: string
  isActive: boolean
  isNpc?: boolean
  controlledByUserId?: string
}

// Interface para mensagem
export interface Message {
  id: string
  tableId: string
  userId: string
  userFullName: string
  characterId?: string
  characterName?: string
  content: string
  messageType: 'narration' | 'dialogue' | 'action' | 'dice-roll' | 'system'
  timestamp: string
  isPrivate: boolean
  recipientId?: string
}

class APIService {
  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    })
  }
  async recoverPassword(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/recover-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }
  private baseURL = API_BASE_URL
  private token: string | null = null

  constructor() {
    // Recuperar token do localStorage na inicialização
    this.token = localStorage.getItem('auth_token')
  }

  // Método para definir token de autenticação
  setToken(token: string) {
    this.token = token
    localStorage.setItem('auth_token', token)
  }

  // Método para remover token
  clearToken() {
    this.token = null
    localStorage.removeItem('auth_token')
  }

  // Método para fazer requisições HTTP
  public async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
        ...options.headers,
      },
      ...options,
    }

    try {
      const response = await fetch(url, config)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error(`Erro na requisição para ${endpoint}:`, errorData)

        // Token expirado ou inválido
        if (response.status === 401 && !endpoint.startsWith('/auth/login')) {
          // Se estiver em modo impersonação, reverter para token original
          const impBackup = sessionStorage.getItem('imp_token_backup')
          if (impBackup) {
            this.setToken(impBackup)
            sessionStorage.removeItem('imp_token_backup')
            sessionStorage.removeItem('imp_info')
            window.location.href = '/admin'
            throw new APIError('Sessão de impersonação expirada. Retornando ao painel admin...', 401)
          }
          this.clearToken()
          window.location.href = '/login'
          throw new APIError('Sessão expirada. Redirecionando para login...', 401)
        }

        // Extrair mensagem de erro do detail (pode ser string ou objeto)
        let errorMessage = `Erro HTTP ${response.status}`
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail
          } else if (errorData.detail.message) {
            errorMessage = errorData.detail.message
          } else if (errorData.detail.error) {
            errorMessage = errorData.detail.error
          }
        }

        // Preservar detail original para tratamento específico
        const error = new APIError(errorMessage, response.status)
        ;(error as any).detail = errorData.detail
        throw error
      }

      // Se não há conteúdo, retorna objeto vazio
      if (response.status === 204) {
        return {} as T
      }

      const data = await response.json()
      return data
    } catch (error) {
      if (error instanceof APIError) {
        throw error
      }

      // Erro de rede ou conexão
      throw new APIError(
        'Erro de conexão com o servidor. Verifique se o backend está rodando.',
        0
      )
    }
  }

  // === AUTENTICAÇÃO ===

  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })

    this.setToken(response.access_token)
    return response
  }

  async register(userData: {
    username: string
    email: string
    full_name: string
    password: string
    role: 'professor' | 'estudante'
  }): Promise<User> {
    return this.request<User>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    })
  }

  async getCurrentUser(): Promise<User> {
    return this.request<User>('/auth/me')
  }

  async updateCurrentUser(updates: Partial<{ full_name: string; username: string; email: string; avatar_url: string; theme: string }>): Promise<User> {
    return this.request<User>('/auth/me', {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  }

  async uploadAvatar(file: File): Promise<{ avatar_url: string }> {
    const formData = new FormData()
    formData.append('avatar', file)

    const url = `${this.baseURL}/users/me/avatar`
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
      },
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new APIError(
        errorData.detail || `Erro ao fazer upload do avatar`,
        response.status
      )
    }

    return response.json()
  }

  async deleteAvatar(): Promise<{ message: string }> {
    return this.request<{ message: string }>('/users/me/avatar', {
      method: 'DELETE',
    })
  }

  async changePassword(current_password: string, new_password: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password, new_password }),
    })
  }

  logout() {
    this.clearToken()
  }

  // === USUÁRIOS ===

  async searchUsers(query: string): Promise<User[]> {
    if (import.meta.env.DEV) {
      console.log(`Buscando usuários com query: ${query}`)
    }
    try {
      const users = await this.request<User[]>(`/users/search?q=${encodeURIComponent(query)}`)
      if (import.meta.env.DEV) {
        console.log('Resposta da busca de usuários:', users)
      }
      return users
    } catch (error) {
      console.error('Erro ao buscar usuários:', error)
      throw error
    }
  }

  // === MESAS/CAMPANHAS ===

  async getUserTables(): Promise<Table[]> {    // Buscar as mesas e mapear campos do backend para o frontend
    const response = await this.request<any[]>('/tables');

    // Mapear resposta para garantir campo user_role
    return response.map(table => ({
      id: table.id,
      name: table.name,
      description: table.description,
      subject: table.subject,
      creator_id: table.createdBy || table.creator_id,
      created_at: table.createdAt || table.created_at,
      image_url: table.imageUrl || table.image_url,
      is_active: table.isActive || table.is_active,
      access_code: table.access_code,
      member_count: table.memberCount || table.member_count || 0,
      character_count: table.characterCount || table.character_count || 0,
      user_role: table.userRole || table.user_role || 'visitante',
      template_id: table.template_id,
      game_config: table.game_config
    }));
  }

  async createTable(tableData: {
    name: string
    description: string
    subject: string
    image_url?: string
    template_id?: string
    game_config?: any
  }, endpoint: string = '/tables'): Promise<Table> {
    return this.request<Table>(endpoint, {
      method: 'POST',
      body: JSON.stringify(tableData),
    })
  }

  async getTemplates(): Promise<GameTemplate[]> {
    return this.request<GameTemplate[]>('/templates')
  }

  async getTableDetails(tableId: string): Promise<{
    id: string
    name: string
    description: string
    subject: string
    image_url?: string
    creator_id: string
    created_at: string
    is_active: boolean
    template_id?: string
    system_setup_complete?: boolean
    character_creation_open?: boolean
    members: Member[]
    characters: Character[]
    user_role?: 'mestre' | 'jogador' | 'visitante'
    access_code?: string
  }> {
    // Log apenas em desenvolvimento
    if (import.meta.env.DEV) {
      console.log(`📞 API - getTableDetails chamada com ID: "${tableId}"`);
    }

    if (!tableId) {
      console.error("❌ API - getTableDetails chamada com ID vazio ou nulo");
      throw new Error("ID da mesa não fornecido");
    }    // Verificar se o ID é um UUID completo, caso contrário, buscar por IDs similares
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    if (!uuidRegex.test(tableId)) {
      console.warn(`⚠️ API - ID recebido não é um UUID válido: "${tableId}". Tentando encontrar correspondência...`);
      // Aqui poderíamos buscar primeiro todas as tabelas e depois filtrar,
      // mas por enquanto vamos apenas lançar um erro mais informativo
      throw new APIError(
        `ID de mesa inválido: ${tableId}. O ID deve ser um UUID completo.`,
        400
      );
    }

    try {
      const result = await this.request<{
        id: string
        name: string
        description: string
        subject: string
        imageUrl?: string
        createdBy: string
        createdAt: string
        isActive: boolean
        templateId?: string
        systemSetupComplete?: boolean
        characterCreationOpen?: boolean
        access_code: string
        members: any[]
        characters: any[]
        userRole?: string
      }>(`/tables/${tableId}`)

      // Mapear resposta para o formato esperado
      return {
        id: result.id,
        name: result.name,
        description: result.description,
        subject: result.subject,
        image_url: result.imageUrl,
        creator_id: result.createdBy,
        created_at: result.createdAt,
        is_active: result.isActive,
        template_id: result.templateId,
        system_setup_complete: result.systemSetupComplete ?? true,
        character_creation_open: result.characterCreationOpen ?? true,
        access_code: result.access_code,
        members: result.members.map(m => ({
          id: m.id,
          tableId: m.tableId || m.table_id,
          userId: m.userId || m.user_id,
          userFullName: m.userFullName || m.user_full_name,
          role: m.role,
          joinedAt: m.joinedAt || m.joined_at,
          isActive: m.isActive || m.is_active
        })),
        characters: result.characters,
        user_role: result.userRole as 'mestre' | 'jogador' | 'visitante'
      }
    } catch (error) {
      console.error(`Erro ao obter detalhes da mesa ${tableId}:`, error)
      throw error
    }
  }

  async updateTable(tableId: string, tableData: {
    name?: string
    description?: string
    subject?: string
    image_url?: string
    is_active?: boolean
  }): Promise<Table> {
    return this.request<Table>(`/tables/${tableId}`, {
      method: 'PUT',
      body: JSON.stringify(tableData)
    })
  }

  /** Abre/fecha a criação de personagens da mesa (interruptor da fase 2, mestre). */
  async setCharacterCreationOpen(tableId: string, open: boolean): Promise<{ characterCreationOpen: boolean }> {
    return this.request(`/tables/${tableId}/character-creation`, {
      method: 'PATCH',
      body: JSON.stringify({ open })
    })
  }

  /** Sorteia personagens na criação mediada (fase 3a, mestre). */
  async sortearCharacters(
    tableId: string,
    opts: { target_user_ids?: string[]; all_without_character?: boolean; role_policy?: 'distinct' | 'free' }
  ): Promise<{ created: any[]; count: number }> {
    return this.request(`/characters/sortear`, {
      method: 'POST',
      body: JSON.stringify({ table_id: tableId, ...opts })
    })
  }

  /** Sorteio cerimonial de UM aluno na vez dele (fase 3a-cerimônia, mestre).
   *  O servidor conduz o ritual pela mesa via WebSocket (dado + revelação). */
  async sortearCerimonia(
    tableId: string,
    targetUserId: string,
    rolePolicy: 'distinct' | 'free' = 'distinct'
  ): Promise<any> {
    return this.request(`/characters/sortear-cerimonia`, {
      method: 'POST',
      body: JSON.stringify({ table_id: tableId, target_user_id: targetUserId, role_policy: rolePolicy })
    })
  }

  async updateTableImage(tableId: string, formData: FormData): Promise<string> {    // Requisição especial que não usa JSON, mas sim FormData para upload de arquivo
    const url = `${this.baseURL}/tables/${tableId}/image`

    try {
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        headers: {
          // Importante: NÃO incluir Content-Type aqui
          // pois o navegador vai definir automaticamente com o boundary correto para o FormData
          Authorization: `Bearer ${this.token}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new APIError(
          errorData.detail || `Erro HTTP ${response.status}`,
          response.status
        )
      }

      const data = await response.json()
      return data.image_url
    } catch (error) {
      console.error('Erro ao atualizar imagem da mesa:', error)
      if (error instanceof APIError) {
        throw error
      }
      throw new APIError('Erro de conexão com o servidor', 0)
    }
  }

  async deleteTable(tableId: string): Promise<{ message: string }> {
    return this.request(`/tables/${tableId}`, {
      method: 'DELETE',
    })
  }

  async duplicateTable(tableId: string): Promise<Table> {
    return this.request<Table>(`/tables/${tableId}/duplicate`, {
      method: 'POST',
    })
  }

  // Ingressar em campanha com código de acesso
  async joinTableByCode(accessCode: string): Promise<{
    message: string
    table: Table
    member: Member
  }> {
    return this.request('/tables/join-by-code', {
      method: 'POST',
      body: JSON.stringify({
        access_code: accessCode.toUpperCase().trim()
      }),
    })
  }

  // Regenerar código de acesso
  async regenerateAccessCode(tableId: string): Promise<{
    message: string
    access_code: string
  }> {
    return this.request(`/tables/${tableId}/regenerate-access-code`, {
      method: 'POST',
    })
  }

  async removeMemberFromTable(tableId: string, memberId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/tables/${tableId}/members/${memberId}`, {
      method: 'DELETE',
    })
  }

  async updateMemberRole(
    tableId: string,
    memberId: string,
    role: 'mestre' | 'jogador'
  ): Promise<Member> {
    return this.request<Member>(`/tables/${tableId}/members/${memberId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    })
  }

  // === PERSONAGENS ===

  async createCharacter(characterData: {
    name: string
    description: string
    class_name: string
    table_id: string
    image_url?: string
    level?: number
    hp_total?: number
    hp_current?: number
    points?: number
    // Atributos dinâmicos (spread do Record<string, number>)
    [key: string]: any
  }): Promise<Character> {
    return this.request<Character>('/characters', {
      method: 'POST',
      body: JSON.stringify(characterData),
    })
  }

  async updateCharacter(
    characterId: string,
    updates: Partial<{
      name: string
      description: string
      class_name: string
      level: number
      hp_total: number
      hp_current: number
      points: number
      // Atributos dinâmicos
      [key: string]: any
    }>
  ): Promise<Character> {
    return this.request<Character>(`/characters/${characterId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  }

  /**
   * Atualização rápida de stats do personagem (PATCH).
   * Usado pelo mestre para alterar HP, pontos, atributos em tempo real.
   * Dispara broadcast WebSocket automaticamente no backend.
   */
  async updateCharacterStats(
    characterId: string,
    stats: {
      hp_current?: number
      hp_total?: number
      points?: number
      level?: number
      attributes?: Record<string, number>
      name?: string        // identidade — mestre edita direto na ficha
      class_name?: string  // idem
    }
  ): Promise<{
    id: string
    name: string
    hpCurrent: number
    hpTotal: number
    points: number
    level: number
    attributes: Record<string, number>
  }> {
    return this.request(`/characters/${characterId}/stats`, {
      method: 'PATCH',
      body: JSON.stringify(stats),
    })
  }

  async deleteCharacter(characterId: string): Promise<{ message: string }> {
    return this.request(`/characters/${characterId}`, {
      method: 'DELETE',
    })
  }

  async uploadCharacterImage(characterId: string, file: File): Promise<{ image_url: string }> {
    const formData = new FormData()
    formData.append('image', file)

    const url = `${this.baseURL}/characters/${characterId}/image`
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
      },
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new APIError(
        errorData.detail || 'Erro ao fazer upload da imagem do personagem',
        response.status
      )
    }

    return response.json()
  }

  async deleteCharacterImage(characterId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/characters/${characterId}/image`, {
      method: 'DELETE',
    })
  }

  // === MENSAGENS ===

  async getTableMessages(
    tableId: string,
    options?: { limit?: number; beforeId?: string; missionId?: string }
  ): Promise<{ messages: Message[]; has_more: boolean }> {
    const params = new URLSearchParams()
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.beforeId) params.append('before_id', options.beforeId)
    // missionId ausente => sem filtro (comportamento atual, I2). 'none' => só as
    // mensagens sem missão (Prólogo).
    if (options?.missionId) params.append('mission_id', options.missionId)
    const queryString = params.toString()
    const url = `/tables/${tableId}/messages${queryString ? `?${queryString}` : ''}`
    return this.request<{ messages: Message[]; has_more: boolean }>(url)
  }

  async createMessage(messageData: {
    table_id: string
    character_id?: string
    content: string
    message_type: 'narration' | 'dialogue' | 'action' | 'dice-roll' | 'system'
    is_private?: boolean
    recipient_id?: string
  }): Promise<Message> {
    return this.request<Message>(`/tables/${messageData.table_id}/messages`, {
      method: 'POST',
      body: JSON.stringify(messageData),
    })
  }

  async updateMessage(messageId: string, content: string): Promise<Message> {
    return this.request<Message>(`/messages/${messageId}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    })
  }

  async deleteMessage(messageId: string): Promise<{ message: string }> {
    return this.request(`/messages/${messageId}`, {
      method: 'DELETE',
    })
  }

  // === GAME CONFIG (Classes e Atributos Dinâmicos) ===

  async completeTableSetup(tableId: string): Promise<any> {
    return this.request<any>(`/tables/${tableId}/complete-setup`, { method: 'POST' })
  }

  async getGameConfig(tableId: string): Promise<any> {
    return this.request<any>(`/tables/${tableId}/game-config`)
  }

  async updateGameConfigSettings(tableId: string, settings: { initial_hp: number }): Promise<any> {
    return this.request<any>(`/tables/${tableId}/game-config/settings`, {
      method: 'PUT',
      body: JSON.stringify(settings)
    })
  }

  async createGameConfigClass(tableId: string, classData: any): Promise<any> {
    return this.request<any>(`/tables/${tableId}/game-config/classes`, {
      method: 'POST',
      body: JSON.stringify(classData)
    })
  }

  async updateGameConfigClass(tableId: string, classId: string, classData: any): Promise<any> {
    return this.request<any>(`/tables/${tableId}/game-config/classes/${classId}`, {
      method: 'PUT',
      body: JSON.stringify(classData)
    })
  }

  async deleteGameConfigClass(tableId: string, classId: string): Promise<any> {
    return this.request<any>(`/tables/${tableId}/game-config/classes/${classId}`, {
      method: 'DELETE'
    })
  }

  async createGameConfigAttribute(tableId: string, attributeData: any): Promise<any> {
    return this.request<any>(`/tables/${tableId}/game-config/attributes`, {
      method: 'POST',
      body: JSON.stringify(attributeData)
    })
  }

  async updateGameConfigAttribute(tableId: string, attributeId: string, attributeData: any): Promise<any> {
    return this.request<any>(`/tables/${tableId}/game-config/attributes/${attributeId}`, {
      method: 'PUT',
      body: JSON.stringify(attributeData)
    })
  }

  async deleteGameConfigAttribute(tableId: string, attributeId: string): Promise<any> {
    return this.request<any>(`/tables/${tableId}/game-config/attributes/${attributeId}`, {
      method: 'DELETE'
    })
  }

  async createGameConfigItem(tableId: string, itemData: any): Promise<any> {
    return this.request<any>(`/tables/${tableId}/game-config/items`, {
      method: 'POST',
      body: JSON.stringify(itemData)
    })
  }

  async updateGameConfigItem(tableId: string, itemId: string, itemData: any): Promise<any> {
    return this.request<any>(`/tables/${tableId}/game-config/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(itemData)
    })
  }

  async deleteGameConfigItem(tableId: string, itemId: string): Promise<any> {
    return this.request<any>(`/tables/${tableId}/game-config/items/${itemId}`, {
      method: 'DELETE'
    })
  }

  // === MISSÕES (capítulos da campanha) — docs/PLANO_MISSOES.md ===

  async getMissions(tableId: string): Promise<Mission[]> {
    return this.request<Mission[]>(`/tables/${tableId}/missions`)
  }

  async createMission(tableId: string, data: { title: string; description?: string }): Promise<Mission> {
    return this.request<Mission>(`/tables/${tableId}/missions`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateMission(
    tableId: string,
    missionId: string,
    data: { title?: string; description?: string; order?: number }
  ): Promise<Mission> {
    return this.request<Mission>(`/tables/${tableId}/missions/${missionId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  async deleteMission(tableId: string, missionId: string): Promise<void> {
    await this.request<void>(`/tables/${tableId}/missions/${missionId}`, {
      method: 'DELETE'
    })
  }

  async activateMission(tableId: string, missionId: string): Promise<Mission> {
    return this.request<Mission>(`/tables/${tableId}/missions/${missionId}/activate`, {
      method: 'POST'
    })
  }

  async closeMission(tableId: string, missionId: string): Promise<Mission> {
    return this.request<Mission>(`/tables/${tableId}/missions/${missionId}/close`, {
      method: 'POST'
    })
  }

  async startMissionSession(tableId: string, missionId: string): Promise<MissionSession> {
    return this.request<MissionSession>(`/tables/${tableId}/missions/${missionId}/sessions/start`, {
      method: 'POST'
    })
  }

  async endMissionSession(tableId: string, missionId: string, sessionId: string): Promise<MissionSession> {
    return this.request<MissionSession>(
      `/tables/${tableId}/missions/${missionId}/sessions/${sessionId}/end`,
      { method: 'POST' }
    )
  }

  async updateMissionSession(
    tableId: string,
    missionId: string,
    sessionId: string,
    data: { summary?: string }
  ): Promise<MissionSession> {
    return this.request<MissionSession>(
      `/tables/${tableId}/missions/${missionId}/sessions/${sessionId}`,
      { method: 'PUT', body: JSON.stringify(data) }
    )
  }

  // === INVENTÁRIO DE PERSONAGENS ===

  async getCharacterInventory(characterId: string): Promise<any[]> {
    return this.request<any[]>(`/characters/${characterId}/inventory`)
  }

  async grantItem(characterId: string, itemId: string, quantity = 1): Promise<any> {
    return this.request<any>(`/characters/${characterId}/inventory`, {
      method: 'POST',
      body: JSON.stringify({ item_id: itemId, quantity })
    })
  }

  async revokeItem(characterId: string, itemId: string, quantity?: number): Promise<any> {
    const qs = quantity != null ? `?quantity=${quantity}` : ''
    return this.request<any>(`/characters/${characterId}/inventory/${itemId}${qs}`, {
      method: 'DELETE'
    })
  }

  // === HABILIDADES DE PERSONAGENS (fase 5 do plano de feedback) ===

  async getCharacterAbilities(characterId: string): Promise<any[]> {
    return this.request<any[]>(`/characters/${characterId}/abilities`)
  }

  async grantAbility(characterId: string, abilityId: string): Promise<any> {
    return this.request<any>(`/characters/${characterId}/abilities`, {
      method: 'POST',
      body: JSON.stringify({ ability_id: abilityId })
    })
  }

  async revokeAbility(characterId: string, abilityId: string): Promise<any> {
    return this.request<any>(`/characters/${characterId}/abilities/${abilityId}`, {
      method: 'DELETE'
    })
  }

  async createGameConfigAbility(tableId: string, abilityData: any): Promise<any> {
    return this.request<any>(`/tables/${tableId}/game-config/abilities`, {
      method: 'POST',
      body: JSON.stringify(abilityData)
    })
  }

  async updateGameConfigAbility(tableId: string, abilityId: string, abilityData: any): Promise<any> {
    return this.request<any>(`/tables/${tableId}/game-config/abilities/${abilityId}`, {
      method: 'PUT',
      body: JSON.stringify(abilityData)
    })
  }

  async deleteGameConfigAbility(tableId: string, abilityId: string): Promise<any> {
    return this.request<any>(`/tables/${tableId}/game-config/abilities/${abilityId}`, {
      method: 'DELETE'
    })
  }

  // === SAÚDE DA API ===

  async healthCheck(): Promise<{ status: string; message: string }> {
    return this.request<{ status: string; message: string }>('/health')
  }

  // === TURNOS ===

  async getTurnState(tableId: string): Promise<any> {
    return this.request<any>(`/tables/${tableId}/turn-state`)
  }

  // Métodos para sistema de dados
  async rollDice(diceData: any): Promise<any> {
    return this.request<any>('/dice/roll', {
      method: 'POST',
      body: JSON.stringify(diceData)
    })
  }

  async getDiceHistory(tableId: string, limit: number = 50): Promise<any> {
    return this.request<any>(`/tables/${tableId}/dice-history?limit=${limit}`)
  }

  // ─── Admin ──────────────────────────────────────────────────
  async adminGetUsers(params?: { search?: string; role?: string; page?: number; per_page?: number }): Promise<any> {
    const qs = new URLSearchParams()
    if (params?.search) qs.set('search', params.search)
    if (params?.role) qs.set('role', params.role)
    if (params?.page) qs.set('page', String(params.page))
    if (params?.per_page) qs.set('per_page', String(params.per_page))
    return this.request<any>(`/admin/users?${qs.toString()}`)
  }

  async adminUpdateUserRole(userId: string, role: string): Promise<any> {
    return this.request<any>(`/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    })
  }

  async adminUpdateUserStatus(userId: string, isActive: boolean): Promise<any> {
    return this.request<any>(`/admin/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: isActive }),
    })
  }

  async adminGetTables(params?: { search?: string; page?: number; per_page?: number }): Promise<any> {
    const qs = new URLSearchParams()
    if (params?.search) qs.set('search', params.search)
    if (params?.page) qs.set('page', String(params.page))
    if (params?.per_page) qs.set('per_page', String(params.per_page))
    return this.request<any>(`/admin/tables?${qs.toString()}`)
  }

  async adminDeleteTable(tableId: string): Promise<any> {
    return this.request<any>(`/admin/tables/${tableId}`, { method: 'DELETE' })
  }

  async adminGetStats(): Promise<any> {
    return this.request<any>('/admin/stats')
  }

  async adminGetEngagement(days: number = 30): Promise<any> {
    return this.request<any>(`/admin/engagement?days=${days}`)
  }

  async adminImpersonate(tableId: string): Promise<{
    token: string
    table_id: string
    table_name: string
    expires_in: number
  }> {
    return this.request(`/admin/impersonate/${tableId}`, {
      method: 'POST',
    })
  }
}

// Instância singleton do serviço
export const apiService = new APIService()

// Função utilitária para verificar se a API está disponível
export async function checkAPIHealth(): Promise<boolean> {
  try {
    await apiService.healthCheck()
    return true
  } catch (error) {
    console.error('API não está disponível:', error)
    return false
  }
}
